import React, { useEffect, useState } from "react";
import { MakeModel, makeAPI } from "../api/makeAPI";
import { Form, InputGroup, Table } from "react-bootstrap";
import MakeModal from "../components/modals/MakeModal";
import ErrorDisplay from "../common/ErrorDisplay";
import LoadingDisplay from "../common/LoadingDisplay";
import { Problem } from "../api/backendApi";
import { PlusCircleFill, Search } from "react-bootstrap-icons";

function MakesPage() {
  const [makes, setMakes] = useState<MakeModel[]>([]);
  const [filteredMakes, setFilteredMakes] = useState<MakeModel[]>([]);
  const [makeCodeFilter, setMakeCodeFilter] = useState<string>("");
  const [makeNameFilters, setMakeNameFilters] = useState<string[]>([""]);
  const [newMake, setNewMake] = useState<MakeModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Problem | undefined>(undefined);
  const [show, setShow] = useState(false);
  const [makeBeingEdited, setMakeBeingEdited] = useState<MakeModel | undefined>(
    undefined,
  );
  const [isCreate, setIsCreate] = useState(false);
  const [modalText, setModalText] = useState("");

  const handleShow = (
    isCreate: boolean,
    modalText: string,
    make?: MakeModel,
  ) => {
    setIsCreate(isCreate);
    setModalText(modalText);
    setNewMake(make);
    setShow(true);
  };

  const filterMakes = () => {
    var filteredMakes = [...makes];
    makeNameFilters.forEach((makeNameFilter) => {
      filteredMakes = filteredMakes.filter(
        (make) =>
          make.code?.startsWith(makeCodeFilter.toUpperCase()) &&
          make.name
            ?.normalize("NFD")
            .replace(/\p{Diacritic}/gu, "")
            .toUpperCase()
            .startsWith(makeNameFilter.toUpperCase()),
      );
    });
    setFilteredMakes(filteredMakes);
  };

  useEffect(() => {
    filterMakes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [makeCodeFilter, makes, makeNameFilters]);

  useEffect(() => {
    async function loadMakes() {
      setLoading(true);
      const data = await makeAPI.getMakes();

      if (data.ok) {
        setError(undefined);
        setMakes(data.value);
        setFilteredMakes(data.value);
      } else {
        setError(data.problem);
      }

      setLoading(false);
    }
    loadMakes();
  }, []);

  const selectMake = (make: MakeModel) => {
    if (make !== makeBeingEdited) setMakeBeingEdited(make);
  };

  const onMakeChanged = (make: MakeModel) => {
    setError(undefined);
    let updatedMakes = makes.map((m: MakeModel) => {
      return m === makeBeingEdited ? make : m;
    });

    setMakes(updatedMakes);
    setMakeBeingEdited(undefined);
  };

  const onNewMake = (make: MakeModel) => {
    setMakes([...makes, make]);
    setNewMake(undefined);

    // 50ms should be enough for everyone
    setTimeout(() => {
      var scrollingElement = document.querySelector("div.fixed-table-header");
      scrollingElement?.scrollTo(0, scrollingElement?.scrollHeight);
    }, 50);
  };

  return (
    <>
      <div className="main-container-title">
        <h1
          style={{
            alignItems: "center",
            justifyContent: "left",
            display: "flex",
          }}
        >
          Makes
          <PlusCircleFill
            className="text-primary"
            onClick={() => handleShow(true, "Add Make", undefined)}
            width={32}
            height={32}
            style={{ marginLeft: "0.5rem" }}
            role="button"
          />
        </h1>

        <ErrorDisplay error={error} />
      </div>
      <div className="main-container-content">
        <LoadingDisplay loading={loading} />

        {!loading && (
          <div
            className="shadow p-3 bg-white rounded"
            style={{ height: "100%" }}
          >
            <div className="fixed-table-header" style={{ height: "100%" }}>
              <Table hover>
                <thead>
                  <tr>
                    <th style={{ width: "150px" }}>
                      <InputGroup className="sm">
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          Code
                        </Form.Label>
                        <span className="input-group-text">
                          <Search width={12} height={12} />
                        </span>
                        <Form.Control
                          size="sm"
                          maxLength={2}
                          onChange={(e) => {
                            setMakeCodeFilter(e.target.value);
                          }}
                        ></Form.Control>
                      </InputGroup>
                    </th>
                    <th>
                      <InputGroup className="sm w-50">
                        <Form.Label
                          style={{
                            marginRight: "1rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          Name
                        </Form.Label>
                        <span className="input-group-text">
                          <Search width={12} height={12} />
                        </span>
                        <Form.Control
                          size="sm"
                          onChange={(e) => {
                            var filters = e.target.value.split(" ");
                            setMakeNameFilters(filters);
                          }}
                        ></Form.Control>
                      </InputGroup>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMakes.map((make, index) => (
                    <tr
                      role="button"
                      key={index}
                      onClick={() => {
                        handleShow(false, "Edit Make", make);
                        selectMake(make);
                      }}
                    >
                      <td>{make.code}</td>
                      <td>{make.name}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
      {show ? (
        <MakeModal
          make={newMake ?? { name: undefined, code: undefined }}
          isCreate={isCreate}
          onChange={isCreate ? onNewMake : onMakeChanged}
          onCancel={() => {
            setNewMake(undefined);
            setShow(false);
          }}
          modalText={modalText}
        />
      ) : null}
    </>
  );
}

export default MakesPage;
